import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Key, Plus, Copy, Search, Monitor, Calendar, Trash2 } from 'lucide-react';
import { usePermission } from '../../hooks/usePermission';

const LicensesPage = () => {
  const queryClient = useQueryClient();
  const { can } = usePermission();
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  
  // Gen Form
  const [genData, setGenData] = useState({ product_id: '', issued_to_email: '', notes: '' });

  const { data: licenses = [] } = useQuery({
    queryKey: ['licenses'],
    queryFn: () => axiosClient.get('/admin/licenses/all')
  });

  const { data: packages = [] } = useQuery({
    queryKey: ['packages'],
    queryFn: () => axiosClient.get('/admin/packages')
  });

  const generateMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/licenses/generate-smart', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['licenses']);
      setShowModal(false);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (key) => axiosClient.delete(`/admin/licenses/${key}`),
    onSuccess: () => {
        queryClient.invalidateQueries(['licenses']);
        queryClient.invalidateQueries(['orders']);
    }
  });

  const handleDelete = (key) => {
      if(window.confirm("DANGER: Deleting this License will also delete the associated Order and Invoice. This cannot be undone. Proceed?")) {
          deleteMutation.mutate(key);
      }
  };

  // ✅ NEW: Copy Logic
  const handleCopy = (text) => {
      navigator.clipboard.writeText(text);
      alert("License Key Copied to Clipboard! 📋");
  };

  const filteredLicenses = licenses.filter(l => 
    l.license_key.toLowerCase().includes(search.toLowerCase()) || 
    (l.issued_to && l.issued_to.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-800">License Management</h1>
        {can('licenses:create') && (
            <button onClick={() => setShowModal(true)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-indigo-700 shadow-lg font-bold text-sm">
            <Plus className="w-4 h-4 mr-2" /> Generate Key
            </button>
        )}
      </div>

      {/* Generator Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-bold mb-4 flex items-center"><Key className="w-5 h-5 mr-2 text-indigo-500"/> Issue New License</h3>
            <form onSubmit={(e) => { e.preventDefault(); generateMutation.mutate(genData); }}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Select Package</label>
                  <select 
                    className="w-full p-2 border rounded mt-1 bg-slate-50"
                    required
                    onChange={e => setGenData({...genData, product_id: parseInt(e.target.value)})}
                  >
                    <option value="">-- Choose a Plan --</option>
                    {packages.map(p => <option key={p.id} value={p.id}>{p.name} ({p.duration_days} Days)</option>)}
                  </select>
                </div>
                <div>
                   <label className="text-sm font-medium text-slate-700">Assign To (Email)</label>
                   <input className="w-full p-2 border rounded mt-1" type="email" placeholder="customer@example.com" required onChange={e => setGenData({...genData, issued_to_email: e.target.value})} />
                </div>
                <div>
                   <label className="text-sm font-medium text-slate-700">Notes (Optional)</label>
                   <input className="w-full p-2 border rounded mt-1" placeholder="e.g. Offline Sale" onChange={e => setGenData({...genData, notes: e.target.value})} />
                </div>
              </div>
              <div className="mt-6 flex space-x-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2 text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
                <button type="submit" className="flex-1 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">Generate</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <input 
          type="text" 
          placeholder="Search by Key or Email..." 
          className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Search className="w-5 h-5 text-slate-400 absolute left-3 top-3.5" />
      </div>

      {/* Licenses Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">License Key</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Plan</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Issued To</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Status</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Active Device</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Expires</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filteredLicenses.map((l) => (
              <tr key={l.license_key} className="hover:bg-slate-50/50 group">
                <td className="px-6 py-4 font-mono text-sm text-slate-700 font-medium flex items-center">
                   {l.license_key.substring(0, 18)}...
                   {/* ✅ FIX: Added onClick Handler */}
                   <button 
                     onClick={() => handleCopy(l.license_key)} 
                     className="ml-2 text-slate-400 hover:text-indigo-600 transition-colors"
                     title="Copy Full Key"
                   >
                     <Copy className="w-3 h-3"/>
                   </button>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">{l.package_name || 'Custom'}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{l.issued_to}</td>
                <td className="px-6 py-4">
                   <span className={`px-2 py-1 rounded text-xs font-bold ${
                     l.status_label === 'Active' ? 'bg-green-100 text-green-700' :
                     l.status_label === 'Pending' ? 'bg-amber-100 text-amber-700' :
                     'bg-red-100 text-red-700'
                   }`}>
                     {l.status_label.toUpperCase()}
                   </span>
                </td>
                <td className="px-6 py-4 text-sm text-slate-500">
                   {l.hostname ? (
                      <div className="flex items-center">
                        <Monitor className="w-3 h-3 mr-1 text-slate-400" /> {l.hostname}
                      </div>
                   ) : '-'}
                </td>
                <td className="px-6 py-4 text-sm text-slate-500 flex items-center">
                   <Calendar className="w-3 h-3 mr-1 text-slate-400"/>
                   {l.expires_at ? new Date(l.expires_at).toLocaleDateString() : 'Lifetime'}
                </td>
                <td className="px-6 py-4 text-right">
                    {can('licenses:revoke') && (
                        <button onClick={() => handleDelete(l.license_key)} className="p-1.5 rounded bg-red-50 text-red-500 hover:bg-red-600 hover:text-white transition-colors">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LicensesPage;